import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import { apiDelete, apiDownload, apiGet, apiUpload } from './api';

const ImportUpload = () => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const navigate = useNavigate();
    const [tradeDirection, setTradeDirection] = useState('IMPORT');
    const [file, setFile] = useState(null);
    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!batch?.id || !['queued', 'processing'].includes(batch.status)) return undefined;
        const timer = setInterval(async () => {
            try {
                const response = await apiGet(`/import/batches/${batch.id}`);
                setBatch(response.json);
            } catch (err) {
                setError(err.message);
            }
        }, 3000);
        return () => clearInterval(timer);
    }, [batch]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!file) {
            setError('Choose an Excel file first.');
            return;
        }
        setLoading(true);
        setError('');
        setBatch(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('trade_direction', tradeDirection);
            const response = await apiUpload('/import/batches', formData);
            setBatch(response.json);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!batch) return;
        setError('');
        try {
            await apiDownload(`/import/batches/${batch.id}/download`, batch.original_filename);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async () => {
        if (!batch) return;
        if (batch.status === 'processing') {
            setError('This batch is still processing. Try again after it finishes.');
            return;
        }
        const confirmed = window.confirm(
            `Delete "${batch.original_filename}" and all imported rows from this batch? This cannot be undone.`
        );
        if (!confirmed) return;

        setDeleting(true);
        setError('');
        try {
            const response = await apiDelete(`/import/batches/${batch.id}`);
            if (response.json?.sourceDeleteError) {
                setError(`Batch data was deleted, but the source file could not be deleted: ${response.json.sourceDeleteError}`);
            }
            setBatch(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 980 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/import')}>
                    Dashboard
                </Button>
                <Typography variant="h6">Upload Trade Excel</Typography>
            </Stack>

            <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Stack component="form" spacing={2} onSubmit={handleSubmit}>
                    <FormControl size="small" sx={{ maxWidth: 240 }}>
                        <InputLabel>Direction</InputLabel>
                        <Select
                            label="Direction"
                            value={tradeDirection}
                            onChange={(event) => setTradeDirection(event.target.value)}
                        >
                            <MenuItem value="IMPORT">Import</MenuItem>
                            <MenuItem value="EXPORT">Export</MenuItem>
                        </Select>
                    </FormControl>
                    <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ width: 'fit-content' }}>
                        Choose Excel
                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(event) => setFile(event.target.files?.[0] || null)}
                        />
                    </Button>
                    {file && (
                        <Typography variant="body2" color="text.secondary">
                            {file.name} ({Math.round(file.size / 1024)} KB)
                        </Typography>
                    )}
                    <Button type="submit" variant="contained" disabled={loading || !file} sx={{ width: 'fit-content' }}>
                        Upload and Process
                    </Button>
                </Stack>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {batch && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={1}>
                        <Typography variant="h6">Batch #{batch.id}</Typography>
                        <Typography>Status: {batch.status}</Typography>
                        <Typography>File: {batch.original_filename}</Typography>
                        <Typography>
                            Rows: {batch.total_rows} | Inserted: {batch.inserted_rows} | Duplicate: {batch.duplicate_rows} | Failed: {batch.failed_rows}
                        </Typography>
                        {['queued', 'processing'].includes(batch.status) && <LinearProgress />}
                        {batch.error_summary && <Alert severity="warning">{batch.error_summary}</Alert>}
                        <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => navigate(`/import/batches/${batch.id}`)}
                            sx={{ width: 'fit-content' }}
                        >
                            Open Batch Details
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            sx={{ width: 'fit-content' }}
                        >
                            Download Source
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            disabled={batch.status === 'processing' || deleting}
                            onClick={handleDelete}
                            sx={{ width: 'fit-content' }}
                        >
                            Delete Batch
                        </Button>
                    </Stack>
                </Paper>
            )}
        </Box>
    );
};

export default ImportUpload;
